import { ExportClient } from 'api/clients/ExportClient'
import { REACT_APP_FRONTEND_ORIGIN } from 'env'
import { AlmanacCableSubscription } from 'providers/CableConsumer/types'
import { WebsocketConsumer } from 'providers/WebsocketProvider'
import { ArticleMeta } from 'types'
import { getOrigin } from 'utils/location'
import { ExportViewState } from './Types'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

type init = {
  article: Pick<ArticleMeta, 'id' | 'title' | 'company_id'>
  websocketConnection: WebsocketConsumer
}

type requestParams = {
  exportOptions: ExportViewState
}

export class Exporter {
  article: Pick<ArticleMeta, 'id' | 'title' | 'company_id'>
  websocketConnection: WebsocketConsumer
  abortController?: AbortController
  wsSubscription?: AlmanacCableSubscription
  exportId?: number
  dismissToast?: any
  flowCanceller?: any

  constructor({ article, websocketConnection }: init) {
    this.article = article
    this.websocketConnection = websocketConnection
    this.exportId = 0
    this.dismissToast = undefined
  }

  async requestExport({ exportOptions }: requestParams) {
    const dismissToast = showToastMessage(
      'Exporting your file...',
      'loading',
      {
        variant: 'cancel',
        label: 'Cancel',
        type: 'button',
        onPress: () => this.cancelExport(),
      },
      20,
      undefined,
    )
    this.dismissToast = dismissToast

    this.abortController = new AbortController()
    try {
      const body = {
        article_keys: [this.article.id],
        export_format: exportOptions.format,
        page_format: exportOptions.pdfPaperSize,
      }
      const { type, data } = await ExportClient.createExport(this.article.company_id, body, this.abortController.signal)
      if (type !== 'success') {
        throw new Error()
      }
      const exportId = data.export_id

      if (exportId) {
        this.exportId = exportId
      }

      if (!exportId) throw new Error('Export ID not found')

      const { fileUrl } = await this.waitForExport(exportId)

      dismissToast()
      showToastMessage(
        'File Exported!',
        'success',
        {
          variant: 'proceed',
          label: 'Download',
          type: 'link',
          to: fileUrl,
        },
        10,
        undefined,
      )
    } catch (err) {
      dismissToast()
      showToastMessage('Export failed', 'error', undefined, 10)
    }
  }

  cancelExport() {
    this.abortController?.abort()
    if (this.exportId) {
      ExportClient.cancelExport(this.exportId)
    }
    if (this.dismissToast) {
      this.dismissToast()
    }
    this.wsSubscription?.unsubscribe()
    showToastMessage('Export canceled', 'info', undefined, 5)
  }

  waitForExport(exportId: number) {
    return new Promise<{ fileUrl: string }>((resolve, reject) => {
      this.wsSubscription = this.websocketConnection?.subscriptions.create(
        { channel: 'ExportChannel', export_id: exportId },
        {
          received: (exportMsg: { export: { file_url: string; errors: string[] } }) => {
            this.wsSubscription?.unsubscribe()
            if (exportMsg.export.errors.length) {
              reject(new Error())
            } else {
              resolve({ fileUrl: exportMsg.export.file_url.replace(REACT_APP_FRONTEND_ORIGIN, getOrigin()) })
            }
          },
        },
      )
    })
  }
}
